import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { usePlatform } from "@/lib/usePlatform";

const OGGI = new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });

const privacy = (p) => (
  <>
    <p>La presente informativa è resa ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (“GDPR”) a chi utilizza l'applicativo gestionale <strong>K Gestionale</strong> (di seguito “App”).</p>
    <h3>1. Titolare del trattamento</h3>
    <p>K Gestionale — contatto: <a href={`mailto:${p.support_email}`} className="text-primary underline">{p.support_email}</a>. Il Titolare può essere contattato a questo indirizzo per ogni richiesta relativa al trattamento dei dati personali.</p>
    <p>Ogni impresa registrata è titolare autonoma dei dati dei propri clienti e fornitori inseriti nell'App; K Gestionale agisce, per tali dati, come Responsabile del trattamento ai sensi dell'art. 28 GDPR.</p>
    <h3>2. Tipologie di dati trattati</h3>
    <ul>
      <li>Dati di account: nome, email, ruolo, credenziali (password conservata in forma cifrata).</li>
      <li>Dati di clienti/fornitori inseriti dall'utente: ragione sociale/nome, P.IVA o codice fiscale, recapiti, indirizzo.</li>
      <li>Dati gestionali: preventivi, fatture, importi, appuntamenti, documenti allegati.</li>
      <li>Dati tecnici: log di accesso e dati necessari alla sicurezza del servizio.</li>
    </ul>
    <h3>3. Finalità e basi giuridiche</h3>
    <ul>
      <li>Erogazione del servizio gestionale (art. 6.1.b — esecuzione del contratto).</li>
      <li>Adempimenti fiscali e amministrativi (art. 6.1.c — obbligo legale).</li>
      <li>Sicurezza, prevenzione abusi e miglioramento del servizio (art. 6.1.f — legittimo interesse).</li>
    </ul>
    <h3>4. Conservazione</h3>
    <p>I dati sono conservati per il tempo necessario alle finalità indicate e, ove applicabile, per i termini di legge (es. documenti contabili e fiscali: 10 anni).</p>
    <h3>5. Comunicazione e responsabili esterni</h3>
    <p>I dati possono essere trattati da fornitori tecnologici (hosting, servizi cloud, elaborazione documenti tramite intelligenza artificiale) nominati Responsabili del trattamento ai sensi dell'art. 28 GDPR. I dati non sono diffusi.</p>
    <h3>6. Trasferimenti extra-UE</h3>
    <p>Qualora alcuni fornitori trattino dati al di fuori dello Spazio Economico Europeo, il trasferimento avviene sulla base di adeguate garanzie (es. Clausole Contrattuali Standard).</p>
    <h3>7. Diritti dell'interessato</h3>
    <p>Hai diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione (artt. 15-22 GDPR), nonché di proporre reclamo all'Autorità Garante per la protezione dei dati personali (www.garanteprivacy.it).</p>
    <h3>8. Natura del conferimento</h3>
    <p>Il conferimento dei dati di account e gestionali è necessario per l'utilizzo dell'App; il mancato conferimento impedisce l'erogazione del servizio.</p>
  </>
);

const termini = (p) => (
  <>
    <p>I presenti Termini e Condizioni regolano l'uso dell'applicativo <strong>K Gestionale</strong>. Per assistenza: <a href={`mailto:${p.support_email}`} className="text-primary underline">{p.support_email}</a>.</p>
    <h3>1. Oggetto</h3>
    <p>L'App consente la gestione di preventivi, fatture, contatti, listini, agenda e report per imprese edili.</p>
    <h3>2. Prova gratuita</h3>
    <p>Le nuove imprese possono provare l'App gratuitamente per 14 giorni. Alla scadenza l'accesso viene sospeso finché l'account non viene attivato; i dati inseriti restano conservati.</p>
    <h3>3. Account e sicurezza</h3>
    <p>L'utente è responsabile della custodia delle proprie credenziali e delle attività svolte con il proprio account. Sono previsti ruoli distinti (Amministratore e Collaboratore) con diversi livelli di accesso ai dati.</p>
    <h3>4. Uso corretto</h3>
    <p>È vietato utilizzare l'App per finalità illecite, caricare contenuti di terzi senza diritto o compromettere la sicurezza del servizio.</p>
    <h3>5. Dati e responsabilità dei contenuti</h3>
    <p>I dati inseriti restano di titolarità dell'utente, che ne garantisce liceità e correttezza. Le funzioni di estrazione automatica tramite intelligenza artificiale sono un ausilio: l'utente è tenuto a verificare importi e descrizioni prima dell'invio o della fatturazione.</p>
    <h3>6. Limitazione di responsabilità</h3>
    <p>Il servizio è fornito “così com'è”. Nei limiti di legge, il fornitore non risponde di danni indiretti derivanti dall'uso o dall'indisponibilità temporanea dell'App.</p>
    <h3>7. Modifiche</h3>
    <p>I presenti Termini possono essere aggiornati; le modifiche saranno rese disponibili in questa pagina.</p>
    <h3>8. Legge applicabile</h3>
    <p>I presenti Termini sono regolati dalla legge italiana. Foro competente: quello previsto dalla legge, salvo foro inderogabile del consumatore.</p>
  </>
);

const cookie = () => (
  <>
    <p>La presente Cookie Policy descrive i cookie e le tecnologie simili utilizzati dall'App <strong>K Gestionale</strong>.</p>
    <h3>1. Cookie tecnici (necessari)</h3>
    <p>Utilizzati per l'autenticazione, il mantenimento della sessione e il funzionamento dell'App (es. token di accesso salvato nel dispositivo). Non richiedono consenso.</p>
    <h3>2. Cookie/tecnologie di preferenza</h3>
    <p>Memorizzano scelte come tema chiaro/scuro e la copia locale dei dati per l'uso offline. Sono attivati con il tuo consenso o come parte del funzionamento richiesto.</p>
    <h3>3. Gestione del consenso</h3>
    <p>Al primo accesso puoi scegliere “Solo necessari” o “Accetta tutti”. Puoi modificare la scelta cancellando i dati del sito dal tuo browser.</p>
    <h3>4. Assenza di profilazione pubblicitaria</h3>
    <p>L'App non utilizza cookie di profilazione pubblicitaria di terze parti.</p>
  </>
);

const DOCS = {
  privacy: { title: "Informativa sulla Privacy", body: privacy },
  termini: { title: "Termini e Condizioni", body: termini },
  cookie: { title: "Cookie Policy", body: cookie },
};

export function Legal({ doc = "privacy" }) {
  const d = DOCS[doc] || DOCS.privacy;
  const platform = usePlatform();
  const loggedIn = typeof window !== "undefined" && !!localStorage.getItem("token");
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <img src="/k-gestionale.png" alt="K Gestionale" className="h-9 w-9 rounded-lg" />
          <div className="font-heading font-extrabold tracking-tight">K Gestionale</div>
          <Link to={loggedIn ? "/" : "/login"} className="ml-auto text-sm text-primary hover:underline flex items-center gap-1" data-testid="legal-back-link">
            <ArrowLeft className="w-4 h-4" /> {loggedIn ? "Torna all'app" : "Torna al login"}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10" data-testid={`legal-${doc}`}>
        <div className="flex items-center gap-2 text-primary mb-2"><ShieldCheck className="w-5 h-5" /><span className="text-xs uppercase tracking-widest font-semibold">Documento legale</span></div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">{d.title}</h1>
        <p className="text-sm text-muted-foreground mt-1">Ultimo aggiornamento: {OGGI}</p>

        <article className="prose prose-sm dark:prose-invert max-w-none mt-8 space-y-4 text-sm leading-relaxed [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:text-muted-foreground [&_li]:text-muted-foreground">
          {d.body(platform)}
        </article>

        <div className="mt-10 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-xs p-4">
          Questo documento è un modello di base predisposto per l'uso dell'App. Prima della commercializzazione a terzi, si consiglia una revisione da parte di un consulente legale per adeguarlo al caso specifico.
        </div>

        <div className="mt-8 flex flex-wrap gap-2 text-base">
          <Link to="/privacy" className="inline-flex items-center min-h-[44px] px-3 rounded-lg text-primary hover:underline hover:bg-secondary">Privacy</Link>
          <Link to="/termini" className="inline-flex items-center min-h-[44px] px-3 rounded-lg text-primary hover:underline hover:bg-secondary">Termini</Link>
          <Link to="/cookie" className="inline-flex items-center min-h-[44px] px-3 rounded-lg text-primary hover:underline hover:bg-secondary">Cookie</Link>
        </div>
      </main>
    </div>
  );
}

export default Legal;
